/**
 * Integer square-grid algebra — pure, render-free, rules-free. The second
 * `Space<P>` realization's building blocks, mirroring `coord.ts`'s role for hex.
 *
 * A `Square` is a plain integer lattice point `{ x, y }` — no `q+r+s=0`
 * invariant, no cube redundancy. Distance is **Chebyshev** (king moves: a
 * diagonal step costs the same as an orthogonal one), so every cell has **8**
 * neighbours and a Size-`N` unit occupies a clean `N×N` block. This is the
 * canonical Lancer "squares on a grid map" reading (core book p. 59), and the
 * exact grid the GM COMBAT.app surface renders.
 *
 * Squares are **immutable value objects**: every operation returns a fresh
 * `Square`, none mutate. Use {@link squareKey} for `Map`/`Set` keys.
 */

/** A cell on the integer square lattice. */
export type Square = {
  readonly x: number;
  readonly y: number;
}

/**
 * Collapse `-0` to `0`. Subtraction/negation readily produce `-0`, which
 * structural compares (`toEqual`) treat as distinct from `0`. Normalising at
 * construction keeps `Square` values canonical so equality is never surprising.
 * (Keys are unaffected — `` `${-0}` `` is already `"0"` — but value identity is.)
 */
function z(n: number): number {
  return n === 0 ? 0 : n;
}

/** Construct a square from integer coords, normalising `-0` to `0`. */
export function square(x: number, y: number): Square {
  return { x: z(x), y: z(y) };
}

/** Stable string key (`"x,y"`) for use in `Map`/`Set`. */
export function squareKey(s: Square): string {
  return `${s.x},${s.y}`;
}

/** Inverse of {@link squareKey}: parse `"x,y"` back into a `Square`. */
export function parseSquareKey(k: string): Square {
  const [x, y] = k.split(",").map(Number);
  if (x === undefined || y === undefined || Number.isNaN(x) || Number.isNaN(y)) {
    throw new Error(`invalid square key: ${JSON.stringify(k)}`);
  }
  return square(x, y);
}

export function equals(a: Square, b: Square): boolean {
  return a.x === b.x && a.y === b.y;
}

/**
 * The eight unit direction vectors (king moves), in counter-clockwise order
 * starting from "east". Index for stepping; map for {@link neighbors}.
 */
export const DIRECTIONS: readonly Square[] = [
  { x: 1, y: 0 },
  { x: 1, y: 1 },
  { x: 0, y: 1 },
  { x: -1, y: 1 },
  { x: -1, y: 0 },
  { x: -1, y: -1 },
  { x: 0, y: -1 },
  { x: 1, y: -1 },
];

/** All eight neighbours of `s` (Chebyshev adjacency), in {@link DIRECTIONS} order. */
export function neighbors(s: Square): Square[] {
  return DIRECTIONS.map((d) => square(s.x + d.x, s.y + d.y));
}

/**
 * The two cells **flanking** a diagonal step `from → to` — the orthogonal cells an
 * L-shaped detour (`from → flank → to`) would route through instead of cutting the
 * corner. For a diagonal step `(dx, dy)` (both non-zero) they are `(from.x+dx, from.y)`
 * and `(from.x, from.y+dy)`; for an **orthogonal** step (one axis unchanged) the move
 * cuts no corner, so there are none (`[]`). Pure geometry — the no-corner-squeeze
 * movement rule (a step is obstructed when *every* flank is blocked) is layered on top
 * in {@link reachableAnchors}, not decided here. Non-adjacent / zero steps also yield `[]`.
 */
export function diagonalFlanks(from: Square, to: Square): Square[] {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  // A corner is cut only by a true diagonal: one cell over on BOTH axes.
  if (Math.abs(dx) !== 1 || Math.abs(dy) !== 1) return [];
  return [square(from.x + dx, from.y), square(from.x, from.y + dy)];
}

/**
 * Chebyshev (chessboard / king-move) distance: `max(|Δx|, |Δy|)`. A diagonal
 * step and an orthogonal step both cost 1, which is what makes 8-neighbour
 * adjacency consistent (every neighbour is at distance 1).
 */
export function distance(a: Square, b: Square): number {
  return Math.max(Math.abs(a.x - b.x), Math.abs(a.y - b.y));
}

/**
 * The straight line of cells from `a` to `b` inclusive (the LoS / push spine),
 * via integer **Bresenham**. Length is exactly `distance(a, b) + 1` and the line
 * is **8-connected** — each consecutive pair is one Chebyshev step apart
 * (diagonal where both axes advance together, orthogonal otherwise). No floats,
 * no rounding ambiguity.
 */
export function line(a: Square, b: Square): Square[] {
  const dx = Math.abs(b.x - a.x);
  const dy = Math.abs(b.y - a.y);
  const sx = a.x < b.x ? 1 : -1;
  const sy = a.y < b.y ? 1 : -1;
  let err = dx - dy;
  let x = a.x;
  let y = a.y;
  const out: Square[] = [];
  for (;;) {
    out.push(square(x, y));
    if (x === b.x && y === b.y) break;
    const e2 = 2 * err;
    if (e2 > -dy) {
      err -= dy;
      x += sx;
    }
    if (e2 < dx) {
      err += dx;
      y += sy;
    }
  }
  return out;
}

/**
 * All cells within Chebyshev radius `r` of `center` (inclusive), `r >= 0` — the
 * `(2r+1)×(2r+1)` axis-aligned block (BLAST/BURST template on a square grid).
 * Returned in a stable column-major order (by `Δx`, then `Δy`).
 */
export function chebyshevDisk(center: Square, r: number): Square[] {
  const out: Square[] = [];
  for (let dx = -r; dx <= r; dx++) {
    for (let dy = -r; dy <= r; dy++) {
      out.push(square(center.x + dx, center.y + dy));
    }
  }
  return out;
}

/**
 * The **free** aim vector from `apex` toward `toward` — the raw integer delta, NOT
 * snapped to any fixed set of directions. `null` when `toward === apex` (no aim).
 *
 * Both directional templates ({@link ray} / {@link cone}) aim off this, so a Line or
 * Cone fires at **whatever angle the player aimed at**, at the resolution the grid
 * itself affords: any `(dx, dy)` from the apex to a cell in reach is a distinct
 * bearing — deliberately *not* collapsed to one of 8 unit directions first, which
 * would make every aim in a 45°-wide fan produce the identical template.
 *
 * Deliberately un-normalized: both templates only ever use this vector in
 * **scale-invariant** ways (`ray` takes its |dx|:|dy| ratio, `cone` compares a dot
 * product against a cross product, and both sides of that comparison scale
 * identically), so dividing through by a length would buy nothing and cost the
 * exact-integer arithmetic.
 */
export function aimVector(apex: Square, toward: Square): Square | null {
  const dx = toward.x - apex.x;
  const dy = toward.y - apex.y;
  if (dx === 0 && dy === 0) return null;
  return square(dx, dy);
}

/**
 * A straight **LINE** template: the `n` cells (`n >= 1`) emanating from `apex` toward
 * `toward`, one cell wide, excluding the apex. The Lancer Line pattern (RAW p.64) is
 * "X spaces long and 1 space wide" and projects its full length in the aimed direction
 * regardless of how near the cursor sits — so this walks `n` cells out along the
 * {@link aimVector} bearing. Empty when `n <= 0` or `toward === apex`.
 *
 * **Off-axis aims are rasterized, not snapped.** The walk is an
 * integer Bresenham DDA: the **major** axis (whichever of `|dx|`/`|dy|` dominates)
 * advances one cell every step, and the **minor** axis advances whenever the
 * accumulated error crosses the half-cell boundary — so step `i` lands on the cell
 * nearest the true geometric ray at that depth. Three properties follow, and all
 * three are load-bearing:
 *
 *   - **Exactly `n` cells, always.** The major axis advancing every step means step
 *     `i` sits at Chebyshev distance exactly `i` from the apex, and Lancer measures
 *     diagonals as 1 space — so `n` steps IS a Line `n`, at every angle. A Line 5
 *     never hits 6 cells because it was aimed obliquely.
 *   - **Connected.** Consecutive cells are always one king-move apart (the major
 *     advances by 1, the minor by 0 or 1), so the template reads as one unbroken beam
 *     with no diagonal gaps.
 *   - **The 8 pure bearings degenerate to the obvious answer.** A cardinal aim has
 *     `minor === 0` (never advances); a true diagonal has `major === minor` (advances
 *     every step). So orthogonal and diagonal Lines are exactly the straight runs an
 *     8-direction template would give; free aiming only *adds* bearings between them.
 *
 * Ties (the ray passing exactly through a cell corner, e.g. a 2:1 aim at odd depths)
 * round the minor axis **away** from the aim axis, consistently — deterministic, and
 * mirror-symmetric under sign flips and under the x/y swap, since the walk is driven
 * purely by `|dx|`/`|dy|` with the signs reapplied at the end.
 */
export function ray(apex: Square, toward: Square, n: number): Square[] {
  if (n <= 0) return [];
  const aim = aimVector(apex, toward);
  if (!aim) return [];
  const ax = Math.abs(aim.x);
  const ay = Math.abs(aim.y);
  const sx = Math.sign(aim.x);
  const sy = Math.sign(aim.y);
  const xMajor = ax >= ay;
  const major = xMajor ? ax : ay; // advances one cell per step
  const minor = xMajor ? ay : ax; // advances when the error crosses the half-cell
  const out: Square[] = [];
  let err = 0;
  let minorSteps = 0;
  for (let i = 1; i <= n; i++) {
    err += minor;
    if (2 * err >= major) {
      minorSteps++;
      err -= major;
    }
    out.push(
      xMajor
        ? square(apex.x + sx * i, apex.y + sy * minorSteps)
        : square(apex.x + sx * minorSteps, apex.y + sy * i),
    );
  }
  return out;
}

/**
 * A **CONE** template: the wedge of cells emanating from `apex` toward `toward`, out
 * to Chebyshev length `n` (`n >= 1`), spreading 45° to each side of the aim axis (a
 * 90° cone). A cell is included when its offset projects forward onto that axis
 * (`along > 0`) and its perpendicular spread does not exceed that forward reach
 * (`|perp| <= along`). The apex is excluded. Empty when `n <= 0` or `toward === apex`.
 * A render/targeting affordance (Blast/Line/Cone previews), not a movement primitive.
 *
 * The axis is the **free** {@link aimVector} — the cone fires at the aimed bearing,
 * not one of 8 snapped ones (matching {@link ray}; a freely-aimed Line beside a cone
 * that still snapped would read as a bug at the table). The wedge test needs no
 * normalization: `along` is a dot product and `perp` a cross product against the SAME
 * vector, so scaling that vector scales both sides of `|perp| <= along` by the
 * identical positive factor — scale-invariant, and exact-integer with an
 * un-normalized aim. On the 8 pure bearings the raw delta is a positive multiple of
 * the unit direction, so those cones are the straight 8-way ones.
 */
export function cone(apex: Square, toward: Square, n: number): Square[] {
  if (n <= 0) return [];
  const aim = aimVector(apex, toward);
  if (!aim) return [];
  const out: Square[] = [];
  for (let ox = -n; ox <= n; ox++) {
    for (let oy = -n; oy <= n; oy++) {
      if (ox === 0 && oy === 0) continue;
      if (Math.max(Math.abs(ox), Math.abs(oy)) > n) continue;
      const along = ox * aim.x + oy * aim.y; // forward projection onto the aim axis
      if (along <= 0) continue;
      const perp = ox * aim.y - oy * aim.x; // signed perpendicular spread off the axis
      if (Math.abs(perp) <= along) out.push(square(apex.x + ox, apex.y + oy));
    }
  }
  return out;
}

/**
 * The cells a unit of Lancer **Size** `size` occupies, anchored at `anchor` — a
 * **mixed model** that keeps the anchor a single well-defined cell at every size:
 *
 * - **Even sizes** are a clean `N×N` block whose **min-corner** (min-x / min-y)
 *   is the anchor. A Size-2 unit at `(x, y)` fills `(x..x+1, y..y+1)`. This
 *   matches Lancer stating Size in square spaces ("N on each side") with no
 *   fractional centre to straddle.
 * - **Odd sizes** are a **centre-clean** concentric block — the anchor is the
 *   middle cell and the block grows symmetrically out to radius `(N-1)/2`. Size 1
 *   is the anchor alone; Size 3 is the 3×3 around it; Size 5 the 5×5.
 *
 * `ceil` collapses Size ½ to a single cell (matching the hex footprint). Either
 * way the count is `ceil(size)²` and the anchor is always a member, so a placed
 * token needs only `(anchor, size)`. Pure integer math.
 */
export function footprint(anchor: Square, size: number): Square[] {
  const n = Math.max(1, Math.ceil(size));
  const out: Square[] = [];
  if (n % 2 === 1) {
    // Odd: concentric block, anchor is the centre cell.
    const r = (n - 1) / 2;
    for (let dx = -r; dx <= r; dx++) {
      for (let dy = -r; dy <= r; dy++) {
        out.push(square(anchor.x + dx, anchor.y + dy));
      }
    }
  } else {
    // Even: clean N×N block, anchor is the min-corner cell.
    for (let dx = 0; dx < n; dx++) {
      for (let dy = 0; dy < n; dy++) {
        out.push(square(anchor.x + dx, anchor.y + dy));
      }
    }
  }
  return out;
}
